<script lang="ts">
  /**
   * RichTextarea — INTERNAL contenteditable primitive.
   *
   * Pages SHOULD NOT import this directly for email-body editing. Use
   * <EmailBodyEditor> instead — it owns the markdown↔HTML bridge and the
   * formatting toolbar so all email-body surfaces stay in sync. If you need a
   * generic rich-text input for some non-email purpose, this primitive is
   * fine to use.
   *
   * What this primitive does: contenteditable div that behaves like a
   * textarea; preserves HTML formatting on paste; two-way binds via `value`
   * (HTML string).
   */
  let {
    value = $bindable(''),
    placeholder = '',
    rows = 8,
    disabled = false,
  }: {
    value: string;
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
  } = $props();

  let el: HTMLDivElement;
  let focused = $state(false);

  // Sync external value changes into the div
  $effect(() => {
    if (el && !focused && el.innerHTML !== value) {
      el.innerHTML = value;
    }
  });

  function handleInput() {
    if (el) {
      value = el.innerHTML;
    }
  }

  function handlePaste(e: ClipboardEvent) {
    // Let the browser handle HTML paste natively (preserves formatting)
    // But sanitize after paste to remove dangerous elements
    setTimeout(() => {
      if (el) {
        sanitize(el);
        value = el.innerHTML;
      }
    }, 0);
  }

  function sanitize(node: HTMLElement) {
    // Remove dangerous tags but keep formatting
    const dangerous = node.querySelectorAll('script, iframe, object, embed, form, input, style');
    dangerous.forEach(el => el.remove());

    // Unwrap legacy <font> tags — keep their text content but lose the
    // font/size/color baked into the tag itself. The toolbar's <b>/<i>
    // wrappers handle real emphasis; <font> only ever carries cosmetic
    // styling that fights the recipient's inbox theme.
    node.querySelectorAll('font').forEach(el => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });

    // Remove event handler attributes, plus inherited background colors,
    // text colors, and font styling. Pasting from Word / Outlook / Google
    // Docs typically embeds `background-color: #fff`, hard-coded `color`,
    // and `font-family: Calibri`-style properties at every level — they
    // paint over the recipient's theme and make the message look like a
    // foreign block in their inbox. Strip them so the recipient's own
    // styling shows through.
    const all = node.querySelectorAll('*');
    all.forEach(el => {
      for (const attr of [...el.attributes]) {
        if (attr.name.startsWith('on') || attr.name === 'srcdoc') {
          el.removeAttribute(attr.name);
        }
      }
      if (el.hasAttribute('bgcolor')) el.removeAttribute('bgcolor');
      if (el.hasAttribute('color')) el.removeAttribute('color');
      if (el.hasAttribute('face')) el.removeAttribute('face');
      if (el.hasAttribute('size')) el.removeAttribute('size');
      const styleAttr = el.getAttribute('style');
      if (styleAttr) {
        const kept = styleAttr.split(';')
          .map(s => s.trim())
          .filter(s => s
            && !/^background(-[a-z]+)?\s*:/i.test(s)
            && !/^color\s*:/i.test(s)
            && !/^font(-[a-z]+)?\s*:/i.test(s))
          .join('; ');
        if (kept) el.setAttribute('style', kept);
        else el.removeAttribute('style');
      }
    });
  }

  function handleFocus() { focused = true; }
  function handleBlur() {
    focused = false;
    if (el) value = el.innerHTML;
  }

  // Convert plain text to minimal HTML if the initial value has no tags
  function initContent(text: string): string {
    if (!text) return '';
    if (/<[a-z][\s\S]*>/i.test(text)) return text; // already HTML
    // Plain text — convert newlines to <br>
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
  }
</script>

<div class="rich-textarea-wrap" class:disabled>
  {#if !value && !focused}
    <div class="rich-textarea-placeholder">{placeholder}</div>
  {/if}
  <div
    bind:this={el}
    class="rich-textarea"
    contenteditable={!disabled}
    role="textbox"
    aria-multiline="true"
    style="min-height: {rows * 1.6}em;"
    oninput={handleInput}
    onpaste={handlePaste}
    onfocus={handleFocus}
    onblur={handleBlur}
  ></div>
</div>

<style>
  .rich-textarea-wrap {
    position: relative;
  }
  .rich-textarea-wrap.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  .rich-textarea {
    padding: 8px 12px;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.6;
    border: 1px solid var(--border, #e2e8f0);
    border-radius: var(--radius-sm, 6px);
    background: var(--bg-surface, #fff);
    color: var(--text, #1e293b);
    outline: none;
    overflow-y: auto;
    max-height: 400px;
    word-break: break-word;
  }
  /* Belt-and-suspenders for paste-from-Word/Outlook/Docs: even if some
     font-family slips past the sanitizer (e.g. via a class that pulled in
     a stylesheet rule we didn't catch), force every descendant to use the
     editor's typography so a pasted fragment doesn't show in Calibri while
     the rest of the email is in the system font. */
  .rich-textarea :global(*) {
    font-family: inherit !important;
    font-size: inherit !important;
    color: inherit !important;
    background: transparent !important;
  }
  /* Preserve emphasis tags that the toolbar uses, even though we just
     reset `font-weight` indirectly via `font: inherit`. (Inherit doesn't
     touch font-weight unless the shorthand was used; explicit override
     here keeps Bold/Italic visible.) */
  .rich-textarea :global(b),
  .rich-textarea :global(strong) { font-weight: bold !important; }
  .rich-textarea :global(i),
  .rich-textarea :global(em) { font-style: italic !important; }
  .rich-textarea:focus {
    border-color: var(--accent, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
  .rich-textarea :global(ul), .rich-textarea :global(ol) {
    margin: 4px 0;
    padding-left: 20px;
  }
  .rich-textarea :global(li) {
    margin: 2px 0;
  }
  .rich-textarea :global(a) {
    color: var(--accent, #2563eb);
  }
  .rich-textarea-placeholder {
    position: absolute;
    top: 8px;
    left: 12px;
    color: var(--text-dim, #94a3b8);
    font-size: 13px;
    pointer-events: none;
    z-index: 1;
  }
</style>
